import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { Folders } from "../api";
import { SYSTEM_NAMES } from "../format";
import type { Route } from "../route";
import { folderRef, FolderMenuButton, type FolderCommand, type FolderRef } from "./FolderMenu";
import { Icon } from "./icons";

const SYSTEM_ICONS: Record<string, string> = { archive: "archive", spam: "spam", trash: "trash" };
const HIDDEN_OPEN = "sidebar.hidden-open";

// Section « Masqués » dépliée ou non : réglage propre à chaque appareil.
function remembered(): boolean {
  try { return localStorage.getItem(HIDDEN_OPEN) === "1"; } catch { return false; }
}
function remember(open: boolean) {
  try { localStorage.setItem(HIDDEN_OPEN, open ? "1" : "0"); } catch { /* navigation privée */ }
}

interface Entry { route: Route; icon: string; folder: FolderRef }

export function Sidebar({ folders, route, go, onNewFolder, onNewMessage, onFolderCommand, footer }: {
  folders: Folders | null;
  route: Route;
  go: (r: Route) => void;
  onNewFolder: () => void;
  onNewMessage: () => void;
  onFolderCommand: (folder: FolderRef, command: FolderCommand) => void;
  footer?: ComponentChildren; // message d'action quand le tiroir est ouvert (téléphone)
}) {
  const [hiddenOpen, setHiddenOpen] = useState(remembered);
  const isActive = (r: Partial<Route>) =>
    route.view === r.view && route.location === r.location && route.address === r.address;
  const item = (r: Route, icon: string, label: string, unread?: number, folder?: FolderRef | null) => (
    <div key={`${r.location}/${r.address ?? ""}`} class={`nav-row${folder ? " has-menu" : ""}`}>
      <button class={`nav-item${isActive(r) ? " active" : ""}`} onClick={() => go(r)} title={label}>
        <Icon name={icon} />
        <span class="label">{label}</span>
        {unread ? <span class="badge">{unread}</span> : null}
      </button>
      {folder && <FolderMenuButton folder={folder} onCommand={onFolderCommand} class="icon-btn nav-more" />}
    </div>
  );
  const show = (e: Entry) => item(e.route, e.icon, e.folder.label, e.folder.unread, e.folder);
  const inbox = folders?.system.inbox;

  // Adresses et dossiers personnalisés, répartis selon leur place.
  const entries: Entry[] = [
    ...(folders?.addresses ?? []).map((a) => ({
      route: { view: "list", location: "inbox", address: a.address } as Route, icon: "at",
      folder: folderRef(folders, "inbox", a.address)!,
    })),
    ...(folders?.custom ?? []).map((f) => ({
      route: { view: "list", location: f.id } as Route, icon: "folder",
      folder: folderRef(folders, f.id)!,
    })),
  ];
  const byLabel = (a: Entry, b: Entry) => a.folder.label.localeCompare(b.folder.label, "fr", { sensitivity: "base" });
  const pinned = entries.filter((e) => e.folder.place === "pinned").sort(byLabel);
  const hidden = entries.filter((e) => e.folder.place === "hidden").sort(byLabel);
  const addresses = entries.filter((e) => e.folder.kind === "address" && e.folder.place === "normal");
  const custom = entries.filter((e) => e.folder.kind === "custom" && e.folder.place === "normal");
  // Le dossier affiché reste visible même s'il est masqué.
  const hiddenShown = hiddenOpen || hidden.some((e) => isActive(e.route));

  return (
    <aside class="sidebar">
      <div class="sidebar-head">
        <span class="brand">Messagerie</span>
        <button class="icon-btn" aria-label="Réglages" onClick={() => go({ view: "settings" })}>
          <Icon name="settings" />
        </button>
      </div>
      <button class="btn primary new-message" onClick={onNewMessage}><Icon name="pen" size={18} />Nouveau message</button>
      <nav aria-label="Dossiers">
        {item({ view: "list", location: "inbox" }, "inbox", SYSTEM_NAMES.inbox, inbox?.unread, folderRef(folders, "inbox"))}
        {item({ view: "list", location: "sent" }, "sent", SYSTEM_NAMES.sent)}
        {item({ view: "list", location: "drafts" }, "draft", SYSTEM_NAMES.drafts, folders?.drafts)}

        {pinned.length > 0 && <div class="nav-title">Épinglés</div>}
        {pinned.map(show)}

        {(addresses.length > 0 || folders?.addresses.length === 0) && <div class="nav-title">Adresses</div>}
        {addresses.map(show)}
        {folders && folders.addresses.length === 0 && <div class="nav-item label">Aucune adresse pour l'instant</div>}

        <div class="nav-title">Dossiers</div>
        {custom.map(show)}
        <button class="nav-item nav-add" onClick={onNewFolder}>
          <Icon name="plus" />
          <span class="label">Nouveau dossier</span>
        </button>

        <div class="nav-title">Rangement</div>
        {["archive", "spam", "trash"].map((l) =>
          item({ view: "list", location: l }, SYSTEM_ICONS[l], SYSTEM_NAMES[l], l === "trash" ? undefined : folders?.system[l]?.unread, folderRef(folders, l)))}

        {hidden.length > 0 && (
          <>
            <button class={`nav-title nav-toggle${hiddenShown ? " open" : ""}`} aria-expanded={hiddenShown}
              onClick={() => { remember(!hiddenShown); setHiddenOpen(!hiddenShown); }}>
              <span>Masqués ({hidden.length})</span>
              <Icon name="chevron" size={16} />
            </button>
            {hiddenShown && hidden.map(show)}
          </>
        )}
      </nav>
      {footer}
    </aside>
  );
}
